import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api';

const CATEGORY_LABELS = {
    'path-to-the-robe': 'The Path to the Robe',
    'as-it-was-taught-to-me': 'As It Was Taught to Me',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS);

const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return isoString;
    }
};

const authorDisplay = (author) => (author === 'brandon' ? 'Brandon' : 'Logan');

const slugify = (title) =>
    title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

const defaultForm = {
    title: '',
    slug: '',
    author: 'brandon',
    category: 'path-to-the-robe',
    status: 'draft',
    excerpt: '',
    content: '',
};

// Markdown preview components (dark theme)
const previewComponents = {
    h1: ({ children }) => <h1 className="text-xl font-bold text-white mt-6 mb-3">{children}</h1>,
    h2: ({ children }) => <h2 className="text-lg font-bold text-white mt-5 mb-2">{children}</h2>,
    h3: ({ children }) => <h3 className="text-base font-semibold text-white mt-4 mb-2">{children}</h3>,
    p: ({ children }) => <p className="text-gray-300 leading-relaxed mb-3 text-sm">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside text-gray-300 mb-3 pl-3 text-sm">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside text-gray-300 mb-3 pl-3 text-sm">{children}</ol>,
    li: ({ children }) => <li className="text-gray-300 text-sm">{children}</li>,
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-amber-500 pl-3 my-3 italic text-gray-400 text-sm">
            {children}
        </blockquote>
    ),
    code: ({ inline, children }) =>
        inline ? (
            <code className="bg-slate-700 text-amber-300 px-1 rounded text-xs font-mono">{children}</code>
        ) : (
            <code className="block bg-slate-800 text-gray-300 p-3 rounded text-xs font-mono overflow-x-auto mb-3 whitespace-pre">
                {children}
            </code>
        ),
    pre: ({ children }) => <pre className="bg-slate-800 rounded overflow-x-auto mb-3">{children}</pre>,
    a: ({ href, children }) => (
        <a href={href} className="text-amber-400 hover:underline text-sm">{children}</a>
    ),
    hr: () => <hr className="border-slate-600 my-4" />,
    strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
    em: ({ children }) => <em className="text-gray-300 italic">{children}</em>,
};

const BlogEditor = () => {
    const { user, isAuthenticated } = useAuth();
    const [view, setView] = useState('list'); // 'list' | 'editor'
    const [posts, setPosts] = useState([]);
    const [isLoadingPosts, setIsLoadingPosts] = useState(true);
    const [listError, setListError] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [editingSlug, setEditingSlug] = useState(null); // null = new post
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState('');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    const fetchPosts = useCallback(async () => {
        setIsLoadingPosts(true);
        setListError(null);
        try {
            const res = await fetch(`${API_URL}/blog?limit=50`);
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const data = await res.json();
            setPosts(data.posts || []);
        } catch {
            setListError('Unable to load posts.');
        } finally {
            setIsLoadingPosts(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !user?.isAdmin) return;
        fetchPosts();
    }, [fetchPosts, isAuthenticated, user]);

    if (!isAuthenticated) {
        return <Navigate to="/auth" replace />;
    }

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-10 text-center max-w-sm">
                    <p className="text-white text-lg mb-4">Administrator access required.</p>
                    <Link to="/" className="text-amber-400 hover:underline text-sm">Return home</Link>
                </div>
            </div>
        );
    }

    const handleNewPost = () => {
        setForm(defaultForm);
        setEditingSlug(null);
        setSlugManuallyEdited(false);
        setSaveError('');
        setSaveSuccess('');
        setView('editor');
    };

    const handleEditPost = (post) => {
        setForm({
            title: post.title || '',
            slug: post.slug || '',
            author: post.author || 'brandon',
            category: post.category || 'path-to-the-robe',
            status: post.status || 'draft',
            excerpt: post.excerpt || '',
            content: post.content || '',
        });
        setEditingSlug(post.slug);
        setSlugManuallyEdited(true);
        setSaveError('');
        setSaveSuccess('');
        setView('editor');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'title' && !slugManuallyEdited) {
            setForm((prev) => ({ ...prev, title: value, slug: slugify(value) }));
        } else if (name === 'slug') {
            setSlugManuallyEdited(true);
            setForm((prev) => ({ ...prev, slug: value }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async (statusOverride) => {
        setSaveError('');
        setSaveSuccess('');
        if (!form.title.trim()) {
            setSaveError('Title is required.');
            return;
        }
        if (!form.slug.trim()) {
            setSaveError('Slug is required.');
            return;
        }

        setIsSaving(true);
        try {
            const { tokens } = await fetchAuthSession();
            const token = tokens.idToken.toString();

            const payload = {
                ...form,
                status: statusOverride || form.status,
            };

            let res;
            if (editingSlug) {
                res = await fetch(`${API_URL}/blog/${editingSlug}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });
            } else {
                res = await fetch(`${API_URL}/blog`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });
            }

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.message || `Save failed (${res.status})`);
            }

            const data = await res.json();
            const savedPost = data.post || data;

            if (!editingSlug) {
                setEditingSlug(savedPost.slug);
            }

            setSaveSuccess(
                statusOverride === 'published'
                    ? 'Post published successfully!'
                    : 'Draft saved successfully!'
            );

            // Refresh list in background
            fetchPosts();
        } catch (err) {
            setSaveError(err.message || 'Failed to save post.');
        } finally {
            setIsSaving(false);
        }
    };

    // -----------------------------------------------------------------------
    // List view
    // -----------------------------------------------------------------------
    if (view === 'list') {
        return (
            <div className="min-h-screen bg-slate-900 py-12 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-1">Blog Editor</h1>
                            <p className="text-gray-400 text-sm">Manage blog posts for both series.</p>
                        </div>
                        <button
                            onClick={handleNewPost}
                            className="bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors"
                        >
                            + New Post
                        </button>
                    </div>

                    {isLoadingPosts && (
                        <div className="flex justify-center py-16">
                            <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                        </div>
                    )}

                    {!isLoadingPosts && listError && (
                        <div className="bg-slate-800/70 rounded-xl p-6 text-center">
                            <p className="text-gray-400">{listError}</p>
                        </div>
                    )}

                    {!isLoadingPosts && !listError && posts.length === 0 && (
                        <div className="bg-slate-800/70 rounded-xl p-10 text-center">
                            <p className="text-gray-400">No posts yet. Create your first post above.</p>
                        </div>
                    )}

                    {!isLoadingPosts && !listError && posts.length > 0 && (
                        <div className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-700">
                                        <th className="text-left text-gray-400 font-medium px-5 py-3">Title</th>
                                        <th className="text-left text-gray-400 font-medium px-5 py-3 hidden md:table-cell">Category</th>
                                        <th className="text-left text-gray-400 font-medium px-5 py-3 hidden sm:table-cell">Author</th>
                                        <th className="text-left text-gray-400 font-medium px-5 py-3">Status</th>
                                        <th className="text-left text-gray-400 font-medium px-5 py-3 hidden md:table-cell">Date</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {posts.map((post) => (
                                        <tr key={post.slug} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                                            <td className="px-5 py-3 text-white font-medium">{post.title}</td>
                                            <td className="px-5 py-3 text-gray-400 hidden md:table-cell">
                                                {CATEGORY_LABELS[post.category] || post.category}
                                            </td>
                                            <td className="px-5 py-3 text-gray-400 hidden sm:table-cell">
                                                {authorDisplay(post.author)}
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${post.status === 'published' ? 'bg-green-900/60 text-green-300' : 'bg-slate-700 text-gray-400'}`}>
                                                    {post.status || 'draft'}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-gray-500 hidden md:table-cell">
                                                {formatDate(post.publishedAt || post.createdAt)}
                                            </td>
                                            <td className="px-5 py-3">
                                                <button
                                                    onClick={() => handleEditPost(post)}
                                                    className="text-amber-400 hover:text-amber-300 text-xs font-medium transition-colors"
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Editor view
    // -----------------------------------------------------------------------
    return (
        <div className="min-h-screen bg-slate-900 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <button
                        onClick={() => { setView('list'); setSaveError(''); setSaveSuccess(''); }}
                        className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                    >
                        &larr; Back to List
                    </button>
                    <div className="flex items-center gap-3">
                        {saveSuccess && (
                            <span className="text-green-400 text-sm">{saveSuccess}</span>
                        )}
                        {saveError && (
                            <span className="text-red-400 text-sm">{saveError}</span>
                        )}
                        <button
                            onClick={() => handleSave('draft')}
                            disabled={isSaving}
                            className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-white text-sm px-4 py-1.5 rounded-full transition-colors"
                        >
                            {isSaving ? 'Saving...' : 'Save Draft'}
                        </button>
                        <button
                            onClick={() => handleSave('published')}
                            disabled={isSaving}
                            className="bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white text-sm px-4 py-1.5 rounded-full font-medium transition-colors"
                        >
                            Publish
                        </button>
                    </div>
                </div>

                {/* Metadata row */}
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-5 mb-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="be-title" className="block text-xs font-medium text-gray-400 mb-1">Title *</label>
                        <input
                            id="be-title"
                            name="title"
                            type="text"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
                            placeholder="Post title"
                        />
                    </div>
                    <div>
                        <label htmlFor="be-slug" className="block text-xs font-medium text-gray-400 mb-1">Slug *</label>
                        <input
                            id="be-slug"
                            name="slug"
                            type="text"
                            value={form.slug}
                            onChange={handleChange}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500 font-mono"
                            placeholder="post-slug"
                        />
                    </div>
                    <div>
                        <label htmlFor="be-author" className="block text-xs font-medium text-gray-400 mb-1">Author</label>
                        <select
                            id="be-author"
                            name="author"
                            value={form.author}
                            onChange={handleChange}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
                        >
                            <option value="brandon">Brandon</option>
                            <option value="logan">Logan</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="be-category" className="block text-xs font-medium text-gray-400 mb-1">Category</label>
                        <select
                            id="be-category"
                            name="category"
                            value={form.category}
                            onChange={handleChange}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="be-status" className="block text-xs font-medium text-gray-400 mb-1">Status</label>
                        <select
                            id="be-status"
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
                        >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                        </select>
                    </div>
                    <div className="sm:col-span-2 md:col-span-3">
                        <label htmlFor="be-excerpt" className="block text-xs font-medium text-gray-400 mb-1">Excerpt</label>
                        <textarea
                            id="be-excerpt"
                            name="excerpt"
                            value={form.excerpt}
                            onChange={handleChange}
                            rows={2}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500 resize-vertical"
                            placeholder="Short summary shown in post cards..."
                        />
                    </div>
                </div>

                {/* Editor + Preview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Editor */}
                    <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-5 flex flex-col">
                        <p className="text-xs font-medium text-gray-400 mb-2">Markdown Content</p>
                        <textarea
                            name="content"
                            value={form.content}
                            onChange={handleChange}
                            rows={20}
                            className="flex-1 bg-slate-700 border border-slate-600 text-white text-sm font-mono rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500 resize-none"
                            placeholder="Write your post content in Markdown..."
                            spellCheck={false}
                        />
                    </div>

                    {/* Preview */}
                    <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-5 overflow-y-auto max-h-[70vh]">
                        <p className="text-xs font-medium text-gray-400 mb-4">Preview</p>
                        {form.content ? (
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={previewComponents}
                            >
                                {form.content}
                            </ReactMarkdown>
                        ) : (
                            <p className="text-gray-600 text-sm italic">Start typing to see a preview...</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogEditor;
